import React from 'react';

import { useHiveClient } from '@gitbook/hive-react';
import type { AgentChangeRequestRpc } from '@services/content-agents/browser';

import { useAgent, useGetAgent } from './useAgent';

type ChangeRequestAgentParams = {
    organizationId: string;
    spaceId: string;
    changeRequestId: string;
};

/**
 * Return a function to imperatively access a change request agent.
 */
export function useGetChangeRequestAgent() {
    const getAgent = useGetAgent();
    const hiveClient = useHiveClient();
    return React.useCallback(
        ({ organizationId, spaceId, changeRequestId }: ChangeRequestAgentParams) => {
            return getAgent<AgentChangeRequestRpc>({
                pathname: `/agents/o/${organizationId}/s/${spaceId}/c/${changeRequestId}`,
                getToken: async () => hiveClient.auth.getToken(spaceId),
            });
        },
        [hiveClient, getAgent]
    );
}

/**
 * Hook to connect to the agent for a change request.
 */
export function useChangeRequestAgent(input: ChangeRequestAgentParams) {
    const { organizationId, spaceId, changeRequestId } = input;
    const hiveClient = useHiveClient();
    const agent = useAgent<AgentChangeRequestRpc>({
        pathname: `/agents/o/${organizationId}/s/${spaceId}/c/${changeRequestId}`,
        getToken: async () => hiveClient.auth.getToken(spaceId),
    });

    return agent;
}
